import {merge, combineLatest} from 'rxjs';
import {filter, map, scan} from 'rxjs/operators';
import {times, toString} from '@/helper/num';
import {wsLogout$} from '@/user/auth-ws';
import {aopMsg$, aopMsgV2$} from '@/raw/aop';
import {contractAccountCurrencies$, contracts$, currenciesCurrencyCache$, riskEngineMode$} from '@/config';
import {accountPosTotalDataCache$} from '@/positions/calc';
import {rasAccountCache$} from '@/ras/ras';
import type {AccountPosTotalData} from '@/positions/calc';
import type {ws, ReduceAction} from '@/types';

export interface ContractAccountRaw2 extends ContractAccountRaw {
  totalCrossPosCostEv?: string;
  totalOrdUsedBalanceEv?: string;
  crossFixedUsedBalanceEv?: string;
  totalPosMarginEv?: string;
}

type ContractAccountRaw = ws.ContractAccountRaw;
type ContractAccountRawV2 = ws.ContractAccountRawV2;
type Cache = Map<string, ContractAccountRaw | ContractAccountRawV2>;
type Action = ReduceAction<Cache>;

const accounts$ = aopMsg$.pipe(
  filter((o) => !!o.accounts),
  map((o) => o.accounts)
);

const accounts2$ = aopMsgV2$.pipe(
  filter((o) => !!o.accounts),
  map((o) => o.accounts)
);

export function convertAccountV2([accounts, riskEngineMode, rasAccountCache, accountPosTotalDataCache = null]) {
  return accounts.map(
    ({
      unrealisedPnlRv,
      userID,
      accountID,
      currency,
      accountBalanceRv,
      totalUsedBalanceRv,
      bonusBalanceRv,
      userMode,
    }) => {
      return innerConvertAccounts(
        {
          userID,
          accountID,
          currency,
          userMode,
          unrealisedPnlEv: unrealisedPnlRv || '0',
          accountBalanceEv: accountBalanceRv || '0',
          totalUsedBalanceEv: totalUsedBalanceRv || '0',
          bonusBalanceEv: bonusBalanceRv || '0',
          isolatedTotalBalanceRv: 0,
          totalContractDebtRv: 0,
          totalInterestRv: 0,
        },
        riskEngineMode ? rasAccountCache.get(currency) : null,
        accountPosTotalDataCache
      );
    }
  );
}

const accountInit$ = contractAccountCurrencies$.pipe(
  map((accountCurrencies): Action => {
    return function (cache: Cache) {
      return Array.from(new Set(accountCurrencies)).reduce((p, currency) => {
        if (p.has(currency)) {
          return p;
        }
        return p.set(currency, {
          accountBalanceEv: 0,
          accountID: 0,
          bonusBalanceEv: 0,
          currency,
          totalUsedBalanceEv: 0,
          userID: 0,
          tradeLevel: 0,
          isolatedTotalBalanceEv: 0,
          userMode: null,
          totalDebtEv: 0,
          totalInterestEv: 0,
        });
      }, cache);
    };
  })
);

const accountUpdate$ = accounts$.pipe(
  map((accounts: ContractAccountRaw[]): Action => {
    return function (cache: Cache) {
      accounts.forEach((account) => cache.set(account.currency, account));
      return cache;
    };
  })
);

const accountUpdate2$ = accounts2$.pipe(
  map((accounts: ContractAccountRawV2[]): Action => {
    return function (cache: Cache) {
      accounts.forEach((account: ContractAccountRawV2) => cache.set(account.currency, account));
      return cache;
    };
  })
);

const accountReset$ = wsLogout$.pipe(
  map((): Action => {
    return function (cache: Cache) {
      cache.clear();
      return cache;
    };
  })
);

const innerContractAccountsCache$ = merge<Action[]>(accountInit$, accountUpdate$, accountUpdate2$, accountReset$).pipe(
  scan((p: Cache, action: Action) => action(p), new Map<string, ContractAccountRaw | ContractAccountRawV2>())
);

export const contractAccountsCache$ = combineLatest([
  innerContractAccountsCache$,
  riskEngineMode$,
  rasAccountCache$,
  accountPosTotalDataCache$,
  currenciesCurrencyCache$,
  contracts$,
]).pipe(
  map(
    ([
      accountsCache,
      riskEngineMode,
      rasAccountCache,
      accountPosTotalDataCache,
      currenciesCurrencyCache,
      contracts,
    ]): Map<string, ContractAccountRaw2> => {
      const cache = new Map();
      accountsCache.forEach((account, key) => {
        const contract = contracts.find((o) => o.settleCurrency === account.currency);
        if (account.hasOwnProperty('accountBalanceRv') || !contract || contract.contractSide > 0) {
          cache.set(key, innerConvertAccountV2([account, riskEngineMode, rasAccountCache, accountPosTotalDataCache]));
        } else {
          const {valueFactor} = currenciesCurrencyCache.get(account.currency) || {valueFactor: 1e8};
          cache.set(key, innerConvertAccountV1([account, rasAccountCache, accountPosTotalDataCache, valueFactor]));
        }
      });
      return cache;
    }
  )
);

function innerConvertAccountV2([account, riskEngineMode, rasAccountCache, accountPosTotalDataCache = null]) {
  const {unrealisedPnlRv, userID, accountID, currency, accountBalanceRv, totalUsedBalanceRv, bonusBalanceRv, userMode} =
    account;
  return innerConvertAccounts(
    {
      userID,
      accountID,
      currency,
      userMode,
      unrealisedPnlEv: unrealisedPnlRv || '0',
      accountBalanceEv: accountBalanceRv || '0',
      totalUsedBalanceEv: totalUsedBalanceRv || '0',
      bonusBalanceEv: bonusBalanceRv || '0',
      isolatedTotalBalanceRv: 0,
      totalContractDebtRv: 0,
      totalInterestRv: 0,
    },
    riskEngineMode ? rasAccountCache.get(currency) : null,
    accountPosTotalDataCache
  );
}

function innerConvertAccountV1([account, rasAccountCache, accountPosTotalDataCache = null, valueFactor]) {
  const {unrealisedPnlEv, userID, currency, accountID, accountBalanceEv, totalUsedBalanceEv, bonusBalanceEv, userMode} =
    account;
  return innerConvertAccounts(
    {
      userID,
      accountID,
      currency,
      userMode,
      unrealisedPnlEv,
      accountBalanceEv,
      totalUsedBalanceEv,
      bonusBalanceEv,
      isolatedTotalBalanceRv: 0,
      totalContractDebtRv: 0,
      totalInterestRv: 0,
    },
    rasAccountCache.get(currency),
    accountPosTotalDataCache,
    valueFactor
  );
}

// 反向合约返回带放大倍数的值，而反向合约account数据带放大倍数、rasAccount数据不带放大倍数，因此需要特殊处理
function innerConvertAccounts(account, rasAccount, accountPosTotalDataCache, valueFactor = 1) {
  const {
    unrealisedPnlEv,
    userID,
    currency,
    accountID,
    accountBalanceEv: accountBalanceEv0,
    totalUsedBalanceEv: totalUsedBalanceEv0,
    bonusBalanceEv: bonusBalanceEv0,
    userMode,
  } = account;

  let accountBalanceEv: string;
  let totalUsedBalanceEv: string;
  let bonusBalanceEv: string;
  let totalDebtEv: string = '0';
  let totalInterestEv: string = '0';
  let isolatedTotalBalanceEv: string = '0';
  let crossFixedUsedBalanceEv: string = '0';
  let totalOrdUsedBalanceEv: string = '0';
  let totalPosMarginEv: string = '0';

  const accountPosTotalData: AccountPosTotalData = accountPosTotalDataCache && accountPosTotalDataCache.get(currency);

  if (rasAccount) {
    accountBalanceEv = times(rasAccount.totalBalanceRv, valueFactor);
    bonusBalanceEv = times(rasAccount.totalBonusRv, valueFactor);
    totalUsedBalanceEv = times(rasAccount.totalUsedBalanceRv, valueFactor);
    totalDebtEv = times(rasAccount.totalContractDebtRv, valueFactor);
    totalInterestEv = times(rasAccount.totalInterestRv, valueFactor);
    isolatedTotalBalanceEv = times(rasAccount.isolatedTotalBalanceRv, valueFactor);
  } else {
    accountBalanceEv = accountBalanceEv0;
    bonusBalanceEv = bonusBalanceEv0;
    totalUsedBalanceEv = totalUsedBalanceEv0;
  }

  if (accountPosTotalData) {
    // 全仓占用 = 全仓仓位占用 + 全仓fixedUsed + 订单占用
    totalUsedBalanceEv = toString(
      accountPosTotalData.totalCrossPosCost
        .plus(accountPosTotalData.totalCrossFixedUsedBalance)
        .plus(accountPosTotalData.totalOrderUsedBalance)
        .times(valueFactor)
    );
    isolatedTotalBalanceEv = times(accountPosTotalData.totalIsolatedTotalBalance, valueFactor);
    crossFixedUsedBalanceEv = times(accountPosTotalData.totalCrossFixedUsedBalance, valueFactor);
    totalOrdUsedBalanceEv = times(accountPosTotalData.totalOrderUsedBalance, valueFactor);
    totalPosMarginEv = times(accountPosTotalData.totalPosMarginEv, valueFactor);
  }
  return {
    accountBalanceEv,
    accountID,
    bonusBalanceEv,
    currency,
    totalUsedBalanceEv,
    unrealisedPnlEv,
    userID,
    userMode,
    isolatedTotalBalanceEv,
    totalCrossPosCostEv: accountPosTotalData ? times(accountPosTotalData.totalCrossPosCost, valueFactor) : '0',
    crossFixedUsedBalanceEv,
    totalOrdUsedBalanceEv,
    totalDebtEv,
    totalInterestEv,
    totalPosMarginEv,
  };
}
